import type { ApiEndpoint } from "@/shared/api-endpoint";
import { isObject } from "@/shared/utils/typeGuards";
import type { SpliceSearchResult } from "@/shared/splice-search.api";

export const kSearchWithSoundSimilarRoute = "/search-with-sound-similar";

export const kMaxResultsRange = { min: 1, max: 25 };
export const kMaxDistanceRange = { min: 0, max: 1 };

export const kSampleFamilySlugs = [
    "bass",
    "brass-and-woodwinds",
    "chords",
    "drums",
    "fx",
    "guitar",
    "keys",
    "leads",
    "others",
    "pads",
    "strings",
    "synth",
    "vocals",
] as const;

export type SampleFamilySlug = (typeof kSampleFamilySlugs)[number];

export type AssetCategorySlug = "oneshot" | "loop";

export interface SearchWithSoundSimilarRequestBody {
    inputFileKey: string;
    maxResults?: number;
    maxDistance?: number;
    assetCategorySlug?: AssetCategorySlug;
    sampleFamilySlug?: SampleFamilySlug;
}

export const isSearchWithSoundSimilarRequestBody = (
    value: unknown,
): value is SearchWithSoundSimilarRequestBody =>
    isObject(value) && typeof value.inputFileKey === "string";

export interface SimilarityDistance {
    readonly uuid: string;
    readonly distance: number;
}

export interface SimilarityMetadata {
    readonly distances: readonly SimilarityDistance[];
    readonly hydratedCount: number;
    readonly assetCategorySlug: string;
    readonly sampleFamilySlug: string;
}

export interface SearchWithSoundSimilarSuccessResponse {
    readonly ok: true;
    readonly results: readonly SpliceSearchResult[];
    readonly similarityMetadata: SimilarityMetadata;
}

export const isSearchWithSoundSimilarSuccessResponse = (
    value: unknown,
): value is SearchWithSoundSimilarSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    Array.isArray(value.results) &&
    isObject(value.similarityMetadata);

export const kSearchWithSoundSimilarEndpoint: ApiEndpoint<
    SearchWithSoundSimilarRequestBody,
    SearchWithSoundSimilarSuccessResponse
> = {
    route: kSearchWithSoundSimilarRoute,
    label: "Search with Sound Similar",
    source: "Atlantis",
    category: "Semantic Intent",
    description:
        "Finds Splice sounds similar to a user-provided audio file using SISO embedding search.",
    isRequestBody: isSearchWithSoundSimilarRequestBody,
    isSuccessResponse: isSearchWithSoundSimilarSuccessResponse,
};
